import React, { type ComponentProps, memo } from 'react';
import { BlurView } from 'expo-blur';
import { StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useStyle } from 'react-native-style-utilities';

import { theme } from '@garnish/constants';

import { useA11ySelector } from '@sg/a11y-helpers';
import { useNavbarContainerHeight } from '@sg/garnish';

import { useFeatureFlag } from '@order/LaunchDarkly';

import { useLocationSearchFlags } from '../../../../../../../../hooks/useLocationSearchFlags';
import {
  LOCATION_RESULTS_BOTTOM_MARGIN_XS_UPDATE,
  LOCATION_SELECTION_UI_BOX_SHADOW_XS,
} from '../../../../../../LocationResults.constants';
import { LocationResultCardLoading } from '../../../../../LocationResultCard';
import { LocationResultsListsV2Card } from '../../../../components';
import { type LocationResultsListsV2Props } from '../../../../LocationResultsListsV2.types';
import { LocationResultsListsV2ExpandButton } from '../LocationResultsListsV2ExpandButton';

// ─────────────────────────────────────────────────────────────────────────────

export const LocationResultsListsV2Collapsed = memo(
  (props: LocationResultsListsV2CollapsedProps) => {
    const {
      locationSearchActorRef,
      variation,
      withSafeAreaInsets,
      focusedLocation,
      canBeExpanded,
      interactedLocationRestaurantSlug,
      interactedLocationDeliveryAddressId,
      onExpand,
    } = props;

    const a11ySelector = useA11ySelector('locations.results.container-collapsed');
    const { offsetStyle: navbarOffsetStyle } = useNavbarContainerHeight();
    const { bottom } = useSafeAreaInsets();
    const safeAreaInsetsStyle = useStyle(() => ({ bottom }), [bottom]);

    const { isLoadingRecentAndNearbyLocations, isLoadingLocations } = useLocationSearchFlags({
      locationSearchActorRef,
    });

    // ─── Flags ───────────────────────────────────────────────────────────

    const isNavbarV2Enabled = useFeatureFlag('CELS-5440-order-navbar-v2-enabled');
    const isLocationSelectionUpdateEnabled = useFeatureFlag('CELS-5577-order-location-selection-ui-update-enabled'); // prettier-ignore

    // ─── Derived ─────────────────────────────────────────────────────────

    const isLoading =
      variation === 'recent-and-nearby' ? isLoadingRecentAndNearbyLocations : isLoadingLocations;
    const shouldOffsetForNavbar = isNavbarV2Enabled && !isLocationSelectionUpdateEnabled;
    const shouldAddModalBottomMargin = isLocationSelectionUpdateEnabled && withSafeAreaInsets;
    const shouldApplySafeAreaInsets = withSafeAreaInsets && !isLocationSelectionUpdateEnabled;

    // ─── Styles ──────────────────────────────────────────────────────────

    const containerStyles = [
      styles.collapsedCardContainer,
      isLocationSelectionUpdateEnabled
        ? styles.collapsedCardContainerUpdate
        : styles.collapsedCardContainerLegacy,
      shouldAddModalBottomMargin ? styles.collapsedCardContainerModalBottomSpacing : undefined,
      isLoading ? undefined : styles.collapsedCardContainerIdle,
    ];
    const containerBackgroundStyles = [
      styles.collapsedCardBackground,
      styles.collapsedCardBackgroundUpdate,
    ];
    const withSafeAreaInsetsStyle = shouldApplySafeAreaInsets ? safeAreaInsetsStyle : undefined;
    const innerContainerStyles = [
      styles.innerContainerCollapsed,
      isLocationSelectionUpdateEnabled ? undefined : styles.innerContainerCollapsedSpacing,
      withSafeAreaInsetsStyle,
      shouldOffsetForNavbar ? navbarOffsetStyle : undefined,
    ];

    // ─────────────────────────────────────────────────────────────────────

    return (
      <View pointerEvents="box-none" style={innerContainerStyles} {...a11ySelector.props}>
        {canBeExpanded ? <LocationResultsListsV2ExpandButton onPress={onExpand} /> : null}

        <View style={containerStyles}>
          {isLocationSelectionUpdateEnabled ? (
            <View style={containerBackgroundStyles} pointerEvents="none">
              <BlurView
                intensity={LOCATION_CONTAINER_BLUR_INTENSITY}
                tint="light"
                style={StyleSheet.absoluteFill}
              />
            </View>
          ) : null}

          {isLoading ? (
            <LocationResultCardLoading />
          ) : (
            <LocationResultsListsV2Card
              locationSearchActorRef={locationSearchActorRef}
              variation={variation}
              location={focusedLocation}
              shouldShowLocationOrderChannel={false}
              interactedLocationRestaurantSlug={interactedLocationRestaurantSlug}
              interactedLocationDeliveryAddressId={interactedLocationDeliveryAddressId}
            />
          )}
        </View>
      </View>
    );
  },
);

// ─── Constants ───────────────────────────────────────────────────────────────

const LOCATION_CONTAINER_BLUR_INTENSITY = 85;

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  collapsedCardContainer: {
    paddingHorizontal: theme.spacing[4],
    borderWidth: 1,
    borderColor: theme.colors.LIGHT,
    borderRadius: theme.radius.large,
  },
  collapsedCardContainerLegacy: {
    backgroundColor: theme.colors.APP_BACKGROUND,
    ...theme.elevations[4],
  },
  collapsedCardContainerUpdate: {
    borderWidth: 0,
    boxShadow: LOCATION_SELECTION_UI_BOX_SHADOW_XS,
  },
  collapsedCardContainerModalBottomSpacing: {
    marginBottom: LOCATION_RESULTS_BOTTOM_MARGIN_XS_UPDATE,
  },
  collapsedCardBackground: {
    ...StyleSheet.absoluteFill,
    borderRadius: theme.radius.large,
    overflow: 'hidden',
  },
  collapsedCardBackgroundUpdate: {
    backgroundColor: 'rgba(250, 250, 246, 0.7)',
  },
  collapsedCardContainerIdle: {
    paddingVertical: theme.spacing[2],
  },
  innerContainerCollapsed: {
    position: 'absolute',
    right: 0,
    bottom: 0,
    left: 0,
    justifyContent: 'flex-end',
    rowGap: theme.spacing[4],
  },
  innerContainerCollapsedSpacing: {
    padding: theme.spacing[4],
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type LocationResultsListsV2CollapsedProps = {
  focusedLocation: ComponentProps<typeof LocationResultsListsV2Card>['location'];
  canBeExpanded: boolean;
  onExpand: () => void;
} & Pick<
  LocationResultsListsV2Props,
  | 'locationSearchActorRef'
  | 'variation'
  | 'withSafeAreaInsets'
  | 'interactedLocationRestaurantSlug'
  | 'interactedLocationDeliveryAddressId'
>;
